import { useState, useEffect, useCallback, useRef } from 'react';
import { X, FileText, ZoomIn, ZoomOut, ChevronLeft, ChevronRight, Loader2, Download, Printer, ExternalLink } from 'lucide-react';
import { cn } from '../lib/utils';
import { isInEmergentPreview } from '../lib/sandboxDetect';
import * as pdfjsLib from 'pdfjs-dist';

pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js`;

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * PDF Canvas Viewer — renders PDF pages on <canvas> via PDF.js.
 */
function PdfCanvasViewer({ blobUrl }) {
  const [pdf, setPdf] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [scale, setScale] = useState(1.5);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const canvasRef = useRef(null);
  const renderTaskRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    pdfjsLib.getDocument(blobUrl).promise.then(pdfDoc => {
      if (!cancelled) { setPdf(pdfDoc); setTotalPages(pdfDoc.numPages); setCurrentPage(1); setLoading(false); }
    }).catch(err => {
      if (!cancelled) { console.error('PDF.js load error:', err); setError('Failed to load PDF'); setLoading(false); }
    });
    return () => { cancelled = true; };
  }, [blobUrl]);

  useEffect(() => {
    if (!pdf || !canvasRef.current) return;
    let cancelled = false;
    (async () => {
      try {
        if (renderTaskRef.current) { renderTaskRef.current.cancel(); renderTaskRef.current = null; }
        const page = await pdf.getPage(currentPage);
        if (cancelled) return;
        const viewport = page.getViewport({ scale });
        const canvas = canvasRef.current;
        canvas.height = viewport.height;
        canvas.width = viewport.width;
        const task = page.render({ canvasContext: canvas.getContext('2d'), viewport });
        renderTaskRef.current = task;
        await task.promise;
      } catch (err) {
        if (err?.name !== 'RenderingCancelledException' && !cancelled) console.error('Render error:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [pdf, currentPage, scale]);

  if (loading) return <div className="flex-1 flex items-center justify-center"><Loader2 className="w-8 h-8 text-white animate-spin" /><p className="text-sm text-gray-300 ml-3">Rendering PDF...</p></div>;
  if (error) return <div className="flex-1 flex items-center justify-center"><p className="text-sm text-red-400">{error}</p></div>;

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="flex items-center justify-center gap-2 py-2 bg-gray-800/80 border-b border-gray-700 flex-shrink-0">
        <button onClick={() => setScale(s => Math.max(0.5, s - 0.25))} className="p-1.5 rounded hover:bg-gray-700 text-gray-300" data-testid="pdf-zoom-out"><ZoomOut className="w-4 h-4" /></button>
        <span className="text-xs text-gray-400 w-14 text-center">{Math.round(scale * 100)}%</span>
        <button onClick={() => setScale(s => Math.min(3, s + 0.25))} className="p-1.5 rounded hover:bg-gray-700 text-gray-300" data-testid="pdf-zoom-in"><ZoomIn className="w-4 h-4" /></button>
        <div className="w-px h-5 bg-gray-600 mx-2" />
        <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage <= 1} className="p-1.5 rounded hover:bg-gray-700 text-gray-300 disabled:opacity-30" data-testid="pdf-prev-page"><ChevronLeft className="w-4 h-4" /></button>
        <span className="text-xs text-gray-300 min-w-[80px] text-center">Page {currentPage} of {totalPages}</span>
        <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage >= totalPages} className="p-1.5 rounded hover:bg-gray-700 text-gray-300 disabled:opacity-30" data-testid="pdf-next-page"><ChevronRight className="w-4 h-4" /></button>
      </div>
      <div className="flex-1 overflow-auto flex justify-center py-4 bg-gray-800/40" data-testid="pdf-canvas-container">
        <canvas ref={canvasRef} className="shadow-2xl" style={{ maxWidth: '100%', height: 'auto' }} data-testid="pdf-canvas" />
      </div>
    </div>
  );
}

/**
 * Global Download Overlay
 * 
 * Viewing: PDF.js on <canvas> (works in any sandbox)
 * Saving: Uploads blob to backend, gets a server URL to an HTML wrapper page.
 * First tries window.open(). If blocked, shows a clickable <a> link.
 * The HTML wrapper embeds the PDF as base64 — McAfee won't flag HTML pages.
 */
export default function DownloadOverlay() {
  const [viewingFile, setViewingFile] = useState(null);
  const [downloadState, setDownloadState] = useState('idle'); // idle | uploading | ready | error
  const [serverPdfUrl, setServerPdfUrl] = useState(null);

  const handleEvent = useCallback((e) => {
    const { url, filename, type } = e.detail;
    const isPdf = type === 'pdf' || filename?.endsWith('.pdf');
    if (isPdf) {
      setViewingFile({ url, filename });
      setDownloadState('idle');
      setServerPdfUrl(null);
    }
  }, []);

  useEffect(() => {
    window.addEventListener('download-ready', handleEvent);
    return () => window.removeEventListener('download-ready', handleEvent);
  }, [handleEvent]);

  const closeViewer = () => {
    if (viewingFile) {
      const url = viewingFile.url;
      setTimeout(() => { try { URL.revokeObjectURL(url); } catch (_) {} }, 2000);
    }
    setViewingFile(null);
    setDownloadState('idle');
    setServerPdfUrl(null);
  };

  const handleSavePrint = async (mode = 'download') => {
    if (!viewingFile || downloadState === 'uploading') return;

    // Sandboxed Emergent preview: downloads + native print are both
    // blocked at the browser-sandbox layer. Tell the user, offer escape
    // to a real tab. The deployed site has no sandbox and works normally.
    if (isInEmergentPreview()) {
      setDownloadState('sandboxed');
      return;
    }

    setDownloadState('uploading');

    try {
      // Fetch blob from blob URL
      const blobResp = await fetch(viewingFile.url);
      const blob = await blobResp.blob();

      // Upload to backend temp store
      const storeResp = await fetch(`${API_URL}/api/store-pdf`, {
        method: 'POST',
        headers: { 'X-Filename': viewingFile.filename || 'document.pdf' },
        body: blob
      });
      if (!storeResp.ok) throw new Error('Upload failed');
      const { pdf_id } = await storeResp.json();

      // Open the wrapper page with `action=download` (or print). The
      // wrapper auto-clicks its real <a download> Save anchor in a
      // top-level browser tab, which is the only reliable way to hit the
      // OS file-save dialog from inside the Emergent preview iframe.
      const pdfPageUrl = `${API_URL}/api/pdf/${pdf_id}?action=${mode === 'print' ? 'print' : 'download'}`;
      setServerPdfUrl(pdfPageUrl);

      const win = window.open(pdfPageUrl, '_blank');
      if (win) {
        // Success! New tab opened — close the overlay.
        closeViewer();
      } else {
        // Popup was blocked — show a clickable link
        setDownloadState('ready');
      }
    } catch (err) {
      console.error('Save/Print failed:', err);
      setDownloadState('error');
    }
  };

  if (!viewingFile) return null;

  // Open the current preview URL in a real browser tab — escapes the
  // Emergent iframe sandbox so the user can actually save/print.
  const openInRealTab = () => {
    try { window.open(window.location.href, '_blank'); } catch { /* */ }
  };

  return (
    <div className="fixed inset-0 z-[99999] flex flex-col bg-black" data-testid="pdf-viewer-modal">
      {/* Sandboxed-preview banner */}
      {downloadState === 'sandboxed' && (
        <div
          className="px-4 py-3 bg-amber-600/90 text-white text-sm flex items-center justify-between gap-3 flex-shrink-0"
          data-testid="pdf-sandbox-banner"
        >
          <div className="flex-1">
            <strong>Downloads don't work in this preview.</strong>{' '}
            The Emergent preview iframe blocks file saves and printing at the browser level.
            Open the app in a real tab — or on your deployed site — and try again.
          </div>
          <button
            onClick={openInRealTab}
            className="px-3 py-1.5 rounded-md bg-white/15 hover:bg-white/25 text-white text-xs font-semibold flex items-center gap-1.5 flex-shrink-0"
            data-testid="pdf-open-real-tab"
          >
            <ExternalLink className="w-3.5 h-3.5" /> Open in new tab
          </button>
        </div>
      )}      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-2.5 bg-gray-900 border-b border-gray-700 flex-shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-8 h-8 rounded-lg bg-rose-500/20 flex items-center justify-center flex-shrink-0">
            <FileText className="w-4 h-4 text-rose-400" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-white truncate" title={viewingFile.filename}>{viewingFile.filename}</p>
            <p className="text-[11px] text-gray-400">PDF Preview</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* State: ready — popup was blocked, show clickable link */}
          {downloadState === 'ready' && serverPdfUrl ? (
            <a
              href={serverPdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-semibold bg-emerald-600 hover:bg-emerald-500 text-white active:scale-[0.97] transition-all"
              data-testid="pdf-open-link"
            >
              <ExternalLink className="w-4 h-4" />
              Click to Open PDF
            </a>
          ) : (
            <>
              <button
                onClick={() => handleSavePrint('download')}
                disabled={downloadState === 'uploading'}
                className={cn(
                  "flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-semibold transition-all",
                  downloadState === 'uploading'
                    ? "bg-emerald-800 text-emerald-300 cursor-wait"
                    : "bg-emerald-600 hover:bg-emerald-500 text-white active:scale-[0.97]"
                )}
                data-testid="pdf-download-btn"
                title="Save the PDF to your computer's Downloads folder"
              >
                {downloadState === 'uploading' ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                {downloadState === 'uploading' ? 'Preparing...' : 'Download'}
              </button>
              <button
                onClick={() => handleSavePrint('print')}
                disabled={downloadState === 'uploading'}
                className={cn(
                  "flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-semibold transition-all",
                  downloadState === 'uploading'
                    ? "bg-blue-800 text-blue-300 cursor-wait"
                    : "bg-blue-600 hover:bg-blue-500 text-white active:scale-[0.97]"
                )}
                data-testid="pdf-print-btn"
                title="Open the OS print dialog"
              >
                <Printer className="w-4 h-4" />
                Print
              </button>
            </>
          )}

          <button
            onClick={closeViewer}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-white transition-colors text-sm font-medium"
            data-testid="pdf-viewer-close-btn"
          >
            <X className="w-4 h-4" />
            Close
          </button>
        </div>
      </div>

      {/* PDF.js viewer */}
      <PdfCanvasViewer blobUrl={viewingFile.url} />
    </div>
  );
}
